import React, { ReactNode } from 'react';
import { BaseErrorBoundary, BaseErrorBoundaryProps, BaseErrorBoundaryState } from './BaseErrorBoundary';
import { clearChunkCache, isChunkLoadError } from '../../utils/lazyWithRetry';

export interface ChunkErrorBoundaryProps extends BaseErrorBoundaryProps {
  maxRetries?: number;
}

export class ChunkErrorBoundary extends BaseErrorBoundary<ChunkErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super({ ...props, errorType: 'Chunk', autoRetry: true, maxRetries: props.maxRetries || 3 });
    this.state = {
      hasError: false,
      errorId: this.generateErrorId(),
      timestamp: new Date(),
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `chunk_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
  }

  protected shouldAutoRetry(error: Error): boolean {
    return isChunkLoadError(error);
  }

  protected renderRetryingUI(): ReactNode {
    return (
      <div
        className={`min-h-[200px] flex items-center justify-center p-6 ${this.props.className || ''}`}
        role="status"
        aria-live="polite"
        aria-label="Retrying"
      >
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <svg className="h-8 w-8 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="currentColor" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p className="text-sm font-medium">Loading page again…</p>
          <p className="text-xs text-gray-500">Attempt {this.state.retryCount + 1} of {this.maxRetries}</p>
        </div>
      </div>
    );
  }

  protected handleChunkRecovery = () => {
    clearChunkCache().then(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('_', String(Date.now()));
      window.location.href = url.pathname + url.search;
    });
  };

  protected renderErrorUI(): ReactNode {
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6 ${this.props.className || ''}`}
        role="alert"
        aria-live="assertive"
        aria-labelledby="error-title"
        aria-describedby="error-description"
      >
        <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl p-8 text-center border border-red-100">
          <div
            className="p-4 bg-red-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center"
            aria-hidden="true"
          >
            <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 id="error-title" className="text-2xl font-bold text-gray-900 mb-3">
            Loading Error
          </h2>

          <p id="error-description" className="text-gray-600 mb-6 leading-relaxed">
            We encountered an error while loading this page. This often happens after an update. Use &quot;Try Again&quot; or
            &quot;Refresh Page&quot; below to load the latest version.
          </p>

          {this.state.errorId && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Error ID:</span> {this.state.errorId}
              </p>
              <p className="text-xs text-gray-500 mt-1">Please include this ID when contacting support.</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={this.handleChunkRecovery}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>

            <button
              onClick={this.handleChunkRecovery}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>

            {this.props.showHomeButton && (
              <button
                onClick={() => (window.location.href = '/')}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go Home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
